export interface CurrencyRates {
  [currency: string]: number;
}

export interface CurrencyResponse {
  result: string;
  base_code: string;
  conversion_rates: CurrencyRates;
}

class CurrencyService {
  private cache: Map<string, { rates: CurrencyRates; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 heures en millisecondes
  private readonly API_BASE_URL = 'https://api.exchangerate-api.com/v4/latest';

  /**
   * Récupère les taux de change pour une devise de base
   * @param baseCurrency Devise de base (ex: 'EUR', 'USD', 'TND')
   * @returns Promise<CurrencyRates>
   */
  async getExchangeRates(baseCurrency: string = 'EUR'): Promise<CurrencyRates> {
    try {
      // Vérifier le cache d'abord
      const cached = this.getCachedRates(baseCurrency);
      if (cached) {
        console.log('💰 Taux de change récupérés depuis le cache');
        return cached;
      }

      console.log(`💰 Récupération des taux de change pour ${baseCurrency}...`);
      
      const response = await fetch(`${this.API_BASE_URL}/${baseCurrency}`);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data: CurrencyResponse = await response.json();
      
      if (data.result !== 'success') {
        throw new Error('Erreur lors de la récupération des taux');
      }

      // Mettre en cache
      this.setCachedRates(baseCurrency, data.conversion_rates);
      
      console.log('💰 Taux de change récupérés avec succès:', data.conversion_rates);
      return data.conversion_rates;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des taux de change:', error);
      
      // Fallback vers des taux fixes en cas d'erreur
      return this.getFallbackRates(baseCurrency);
    }
  }

  /**
   * Convertit un montant d'une devise vers une autre
   * @param amount Montant à convertir
   * @param fromCurrency Devise source
   * @param toCurrency Devise cible
   * @returns Promise<number>
   */
  async convertCurrency(
    amount: number, 
    fromCurrency: string, 
    toCurrency: string
  ): Promise<number> {
    try {
      // Si les devises sont identiques, retourner le montant original
      if (fromCurrency === toCurrency) {
        return amount;
      }

      const rates = await this.getExchangeRates(fromCurrency);
      const rate = rates[toCurrency];

      if (!rate) {
        throw new Error(`Taux de change non disponible pour ${toCurrency}`);
      }

      const convertedAmount = amount * rate;
      console.log(`💰 Conversion: ${amount} ${fromCurrency} = ${convertedAmount.toFixed(2)} ${toCurrency}`);
      
      return convertedAmount;
    } catch (error) {
      console.error('❌ Erreur lors de la conversion:', error);
      throw error;
    }
  }

  /**
   * Formate un montant avec le symbole de la devise
   * @param amount Montant
   * @param currency Code de la devise
   * @returns string
   */
  formatCurrency(amount: number, currency: string): string {
    const symbols: { [key: string]: string } = {
      'EUR': '€',
      'USD': '$',
      'TND': 'د.ت',
      'GBP': '£',
      'JPY': '¥'
    };

    const symbol = symbols[currency] || currency;
    const formattedAmount = amount.toFixed(2);
    
    return `${symbol}${formattedAmount}`;
  }

  /**
   * Récupère les taux depuis le cache
   * @param baseCurrency Devise de base
   * @returns CurrencyRates | null
   */
  private getCachedRates(baseCurrency: string): CurrencyRates | null {
    const cached = this.cache.get(baseCurrency);
    
    if (!cached) {
      return null;
    }

    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_DURATION) {
      // Cache expiré, le supprimer
      this.cache.delete(baseCurrency);
      return null;
    }

    return cached.rates;
  }

  /**
   * Met en cache les taux de change
   * @param baseCurrency Devise de base
   * @param rates Taux de change
   */
  private setCachedRates(baseCurrency: string, rates: CurrencyRates): void {
    this.cache.set(baseCurrency, {
      rates,
      timestamp: Date.now()
    });
  }

  /**
   * Taux de fallback en cas d'erreur API
   * @param baseCurrency Devise de base
   * @returns CurrencyRates
   */
  private getFallbackRates(baseCurrency: string): CurrencyRates {
    const fallbackRates: { [key: string]: CurrencyRates } = {
      'EUR': {
        'USD': 1.08,
        'TND': 3.45,
        'GBP': 0.86,
        'JPY': 160.50,
        'EUR': 1.0
      },
      'USD': {
        'EUR': 0.93,
        'TND': 3.20,
        'GBP': 0.80,
        'JPY': 148.60,
        'USD': 1.0
      },
      'TND': {
        'EUR': 0.29,
        'USD': 0.31,
        'GBP': 0.23,
        'JPY': 46.52,
        'TND': 1.0
      }
    };

    return fallbackRates[baseCurrency] || fallbackRates['EUR'];
  }

  /**
   * Nettoie le cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('💰 Cache des taux de change vidé');
  }

  /**
   * Obtient les devises supportées
   * @returns string[]
   */
  getSupportedCurrencies(): string[] {
    return ['EUR', 'USD', 'TND', 'GBP', 'JPY'];
  }
}

// Instance singleton
export const currencyService = new CurrencyService();
export default currencyService; 